// ui/src/components/shared/TemporalRelationshipBadge.jsx

/**
 * @file TemporalRelationshipBadge.jsx
 * @description Canonical renderer for server-supplied temporal applicability.
 *   Accepts a TemporalRelationshipPayload and renders a styled badge + detail
 *   line for all 8 recognised relationship values. Does NOT derive temporal
 *   state from planStatus — relationship must be supplied by the server.
 *   No API calls — prop-driven display only.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see decisionflow_temporal_contract_v1_1_final.md — 9 temporal invariants
 * @see api_spec_fileB_v4_final.md §6.2 — temporalRelationship field (ep 49)
 */

import React from 'react';

/**
 * @typedef {'governs_current'|'governs_through'|'governs_from'|'future_effective'|'historical'|'split_v1'|'split_v2'|'draft'} TemporalRelationship
 *
 * @typedef {Object} TemporalRelationshipPayload
 * @property {TemporalRelationship} relationship
 * @property {string|null} effectiveFrom
 * @property {string|null} effectiveTo
 * @property {number} [version]
 * @property {string} [referenceDate]
 */

/**
 * @param {string|null|undefined} isoDate
 * @returns {string|null}
 */
function formatDate(isoDate) {
  if (!isoDate) return null;

  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const RELATIONSHIP_STYLES = {
  governs_current: {
    background: 'rgb(var(--color-success) / 0.12)',
    border: 'rgb(var(--color-success) / 0.35)',
    color: 'rgb(var(--color-success))',
  },
  governs_through: {
    background: 'rgb(var(--color-warning) / 0.12)',
    border: 'rgb(var(--color-warning) / 0.35)',
    color: 'rgb(var(--color-warning))',
  },
  governs_from: {
    background: 'rgb(var(--color-warning) / 0.12)',
    border: 'rgb(var(--color-warning) / 0.35)',
    color: 'rgb(var(--color-warning))',
  },
  future_effective: {
    background: 'rgb(var(--color-info) / 0.12)',
    border: 'rgb(var(--color-info) / 0.35)',
    color: 'rgb(var(--color-info))',
  },
  historical: {
    background: 'rgb(var(--color-surface-muted))',
    border: 'rgb(var(--color-border))',
    color: 'rgb(var(--color-text-muted))',
  },
  split_v1: {
    background: 'rgb(var(--color-info) / 0.12)',
    border: 'rgb(var(--color-info) / 0.35)',
    color: 'rgb(var(--color-info))',
  },
  split_v2: {
    background: 'rgb(var(--color-info) / 0.12)',
    border: 'rgb(var(--color-info) / 0.35)',
    color: 'rgb(var(--color-info))',
  },
  draft: {
    background: 'rgb(var(--color-surface-muted))',
    border: 'rgb(var(--color-border))',
    color: 'rgb(var(--color-text-muted))',
  },
};

/**
 * Canonical renderer for server-supplied temporal applicability.
 *
 * IMPORTANT:
 * This component deliberately does not accept planStatus and does not derive
 * temporal applicability. `relationship` is part of the temporal contract.
 *
 * @param {{
 *   temporalRelationship: TemporalRelationshipPayload,
 *   compact?: boolean,
 *   className?: string
 * }} props
 */
export default function TemporalRelationshipBadge({
  temporalRelationship,
  compact = false,
  className = '',
}) {
  const {
    relationship,
    effectiveFrom,
    effectiveTo,
    version,
    referenceDate,
  } = temporalRelationship || {};

  const from = formatDate(effectiveFrom);
  const to = formatDate(effectiveTo);
  const reference = formatDate(referenceDate);

  let label;
  let detail;

  switch (relationship) {
    case 'governs_current':
      label = 'Governing';
      detail = from
        ? `Effective from ${from}${to ? ` through ${to}` : ', open-ended'}`
        : 'Current governing effective window';
      break;

    case 'governs_through':
      label = `Governs events through ${to || 'effective end date unavailable'}`;
      detail = from
        ? `Effective window began ${from}`
        : 'Transitional governing version';
      break;

    case 'governs_from':
      label = `Governs events from ${from || 'effective start date unavailable'}`;
      detail = to
        ? `Effective through ${to}`
        : 'Effective window is open-ended';
      break;

    case 'future_effective':
      label = `Effective ${from || 'date unavailable'}`;
      detail = 'Future effective window — not currently governing';
      break;

    case 'historical':
      label = `Governed ${from || 'unknown'}–${to || 'unknown'}`;
      detail = 'Historical effective window';
      break;

    case 'split_v1':
      label = `V${version ?? '?'} · through ${to || 'date unavailable'}`;
      detail = from
        ? `First split-period version, effective from ${from}`
        : 'First split-period version';
      break;

    case 'split_v2':
      label = `V${version ?? '?'} · from ${from || 'date unavailable'}`;
      detail = to
        ? `Second split-period version, effective through ${to}`
        : 'Second split-period version';
      break;

    case 'draft':
      label = 'Draft — not governing';
      detail = 'No approved effective window';
      break;

    default:
      label = 'Temporal relationship unavailable';
      detail = 'No recognized temporal relationship was supplied';
  }

  const visual =
    RELATIONSHIP_STYLES[relationship] || RELATIONSHIP_STYLES.draft;

  const ariaLabel = [
    label,
    detail,
    reference ? `Reference date ${reference}` : null,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <span
      className={`inline-flex flex-col items-start gap-1 ${className}`}
      aria-label={ariaLabel}
    >
      <span
        className="inline-flex items-center rounded-[999px] border px-[10px] py-[2px] text-[11px] font-medium leading-4"
        style={{
          backgroundColor: visual.background,
          borderColor: visual.border,
          color: visual.color,
        }}
      >
        {label}
      </span>

      {!compact && (
        <span className="text-[11px] leading-4 text-[rgb(var(--color-text-muted))]">
          {detail}
        </span>
      )}
    </span>
  );
}
